import React, { useEffect, useRef, useState } from "react";
import { Grow } from "@mui/material";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Avatar,
  Tooltip,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchComments,
  createComment,
  updateComment,
  deleteComment,
  clearCommentError,
  resetComments,
} from "../features/comment/commentSlice";

const LOAD_TRIGGER_PX = 100;

const CommentsSection = ({ postId, contentId }) => {
  const dispatch = useDispatch();
  const { items, loading, error, hasMore, skip, limit } = useSelector(
    (state) => state.comment
  );
  const { user } = useSelector((state) => state.auth);

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [text, setText] = useState("");
  const [editId, setEditId] = useState(null);
  const listRef = useRef(null);

  // Reset comments when topic changes
  useEffect(() => {
    dispatch(resetComments());
    if (postId) {
      dispatch(fetchComments({ postId, skip: 0, limit }));
    }
  }, [dispatch, postId, limit]);

  // Infinite scroll handler
  useEffect(() => {
    const ref = listRef.current;
    if (!ref) return;

    const handleScroll = () => {
      if (loading || !hasMore) return;
      const { scrollTop, scrollHeight, clientHeight } = ref;
      if (scrollHeight - scrollTop - clientHeight < LOAD_TRIGGER_PX) {
        dispatch(fetchComments({ postId, skip, limit }));
      }
    };

    ref.addEventListener("scroll", handleScroll);
    return () => ref.removeEventListener("scroll", handleScroll);
  }, [dispatch, postId, skip, limit, hasMore, loading]);

  const formatDateOrTime = (timestamp) => {
    const dateObj = new Date(timestamp);
    const today = new Date();
    const isToday =
      dateObj.getDate() === today.getDate() &&
      dateObj.getMonth() === today.getMonth() &&
      dateObj.getFullYear() === today.getFullYear();
    return isToday
      ? dateObj.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : dateObj.toLocaleDateString();
  };

  const shortenName = (name) => {
    if (!name) return "";
    return name.length > 10 ? name.substring(0, 10) + "…" : name;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!user) {
      alert("You must be logged in to comment");
      return;
    }
    if (editId) {
      dispatch(
        updateComment({
          comment_id: editId,
          text,
          post_id: postId,
          content_id: contentId,
        })
      );
      setEditId(null);
    } else {
      dispatch(createComment({ text, post_id: postId, content_id: contentId }));
    }
    setText("");
  };

  const handleEdit = (comment) => {
    setText(comment.text);
    setEditId(comment.id);
  };

  const handleDelete = (id) => {
    if (window.confirm("Delete this comment?")) {
      dispatch(deleteComment(id));
    }
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {error && (
        <Alert
          severity="error"
          onClose={() => dispatch(clearCommentError())}
          sx={{ mb: 1 }}
        >
          {error}
        </Alert>
      )}

      {/* Comments List */}
      <Box
        ref={listRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          mb: 1,
          backgroundColor: theme.palette.background.paper,
          transition: "background-color 0.3s ease, color 0.3s ease",
          ...{
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.mode === "light" ? "#bbb" : "#555",
              borderRadius: "6px",
            },
          },
        }}
      >
        {items.length === 0 && !loading && (
          <Typography sx={{ p: 2, color: "text.secondary" }}>
            No comments yet.
          </Typography>
        )}

        {items.map((c) => (
          <Grow key={c.id} in mountOnEnter unmountOnExit timeout={500}>
            <Box
              sx={{
                borderBottom: `1px solid ${theme.palette.divider}`,
                p: 1.2,
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                transition: "background-color 0.2s ease",
                "&:hover": {
                  backgroundColor: isDark
                    ? "rgba(255,255,255,0.04)"
                    : "rgba(0,0,0,0.02)",
                },
              }}
            >
              {/* Avatar */}
              <Avatar
                src={c.user_picture || "/default-avatar.png"}
                alt={c.username}
                sx={{
                  width: 36,
                  height: 36,
                  border: `2px solid ${theme.palette.primary.main}`,
                  bgcolor: theme.palette.background.default,
                  flexShrink: 0,
                }}
              />

              {/* Comment Body */}
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Tooltip title={c.username}>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color="primary"
                      sx={{ textTransform: "capitalize" }}
                    >
                      {shortenName(c.username)}
                    </Typography>
                  </Tooltip>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    {formatDateOrTime(c.timestamp)}
                  </Typography>

                  {user && user.username === c.username && (
                    <Box sx={{ flexShrink: 0 }}>
                      <Tooltip title="Edit comment">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(c)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete comment">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(c.id)}
                          sx={{ color: "error.main" }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>

                {/* Comment text */}
                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.8,
                    backgroundColor: isDark
                      ? "rgba(255,255,255,0.08)"
                      : theme.palette.grey[50],
                    borderRadius: 2,
                    p: 1.2,
                    color: theme.palette.text.primary,
                    whiteSpace: "pre-wrap",
                    boxShadow: isDark
                      ? "inset 0 0 4px rgba(255,255,255,0.05)"
                      : "inset 0 0 4px rgba(0,0,0,0.05)",
                  }}
                >
                  {c.text}
                </Typography>
              </Box>
            </Box>
          </Grow>
        ))}

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
            <CircularProgress size={20} />
          </Box>
        )}
      </Box>

      {/* Comment Form */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: "flex",
          gap: 1,
          mt: "auto",
          backgroundColor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider}`,
          pt: 1,
          pb: 1.5,
        }}
      >
        <TextField
          label={editId ? "Edit comment" : "Add a comment"}
          value={text}
          onChange={(e) => setText(e.target.value)}
          fullWidth
          multiline
          minRows={1}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: theme.palette.background.default,
              "& fieldset": {
                borderColor: theme.palette.divider,
              },
              "&:hover fieldset": {
                borderColor: theme.palette.primary.main,
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={!user || text.trim() === ""}
          sx={{
            textTransform: "none",
            px: 2.5,
            borderRadius: 2,
            backgroundColor: theme.palette.primary.main,
            "&:hover": { backgroundColor: theme.palette.primary.dark },
          }}
        >
          {editId ? "Update" : "Post"}
        </Button>
      </Box>
    </Box>
  );
};

export default CommentsSection;
